import { useState, useRef, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import api from "../../../api";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

const ProfileInfo = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    username: user?.username || "",
    email: user?.email || "",
    role: user?.role || "",
    phone: user?.phone || "",
    timezone: user?.timezone || "UTC+00:00",
    imageUrl: user?.imageUrl || "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
        timezone: user.timezone || "UTC+00:00",
        imageUrl: user.imageUrl || "",
      });
    }
  }, [user]);

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/update-profile", {
        username: form.username,
        phone: form.phone,
        timezone: form.timezone,
      });
      toast.success("Profile updated successfully!");
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPG and PNG files are allowed");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File must be under 2MB");
      return;
    }

    const storage = getStorage();
    const storageRef = ref(
      storage,
      `admin_images/${user.uid}/profile_${user.uid}`,
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    let realProgress = 0;
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= realProgress) return prev;
        return Math.min(prev + 1, realProgress);
      });
    }, 20);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        realProgress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
      },
      (err) => {
        clearInterval(interval);
        toast.error("Upload failed: " + err.message);
        setUploadProgress(null);
      },
      async () => {
        realProgress = 100;

        const waitForComplete = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 100) {
              clearInterval(interval);
              clearInterval(waitForComplete);
              return 100;
            }
            return prev + 1;
          });
        }, 20);

        setTimeout(async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await api.patch("/auth/update-profile", {
            imageUrl: downloadURL,
          });
          toast.success("Photo updated!");
          setUploadProgress(null);
          setForm((prev) => ({ ...prev, imageUrl: downloadURL }));
        }, 800);
      },
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {/* ── PROFILE INFO CARD ── */}
      <div className="profile-card">
        <div className="profile-card-header">
          <div>
            <h3>Profile Information</h3>
            <p>Update your personal information and profile details.</p>
          </div>
          {!editing ? (
            <Button
              variant="outlined"
              color="neutral"
              size="small"
              startIcon={
                <span className="material-symbols-outlined">edit</span>
              }
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          ) : (
            <div className="edit-actions">
              <Button
                variant="outlined"
                color="neutral"
                size="small"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </div>

        <div className="profile-card-body">
          {/* Photo */}
          <div className="photo-section">
            <p className="field-label">Profile Photo</p>
            <div className="photo-wrapper">
              <Avatar
                src={form.imageUrl}
                sx={{ width: 90, height: 90, bgcolor: "#006663", fontSize: 32 }}
              >
                {user?.username?.charAt(0)?.toUpperCase() || "A"}
              </Avatar>
              {uploadProgress !== null && (
                <div className="upload-overlay">
                  <span className="upload-percent">{uploadProgress}%</span>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileRef}
              hidden
              accept="image/jpeg, image/png"
              onChange={handlePhotoUpload}
            />
            <Button
              variant="outlined"
              color="neutral"
              size="small"
              disabled={uploadProgress !== null}
              startIcon={
                <span className="material-symbols-outlined">upload</span>
              }
              onClick={() => fileRef.current?.click()}
            >
              {uploadProgress !== null ? `${uploadProgress}%` : "Upload"}
            </Button>
            <p className="photo-hint">JPG, PNG up to 2MB</p>
          </div>

          {/* Fields */}
          <div className="profile-fields">
            <div className="field-row">
              <div className="field-group">
                <label className="field-label">Full Name</label>
                <input
                  name="username"
                  value={form.username}
                  onChange={handleFormChange}
                  disabled={!editing || user?.role !== "super_admin"}
                  className="field-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label">Email Address</label>
                <input
                  name="email"
                  value={form.email}
                  disabled
                  className="field-input disabled"
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field-group">
                <label className="field-label">Role</label>
                <input
                  name="userRole"
                  value={
                    user?.role === "super_admin"
                      ? "Super Admin"
                      : user?.role === "moderator"
                        ? "Moderator"
                        : "Admin"
                  }
                  disabled
                  className="field-input disabled"
                />
              </div>
              <div className="field-group">
                <label className="field-label">Phone Number</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleFormChange}
                  disabled={!editing}
                  className="field-input"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field-group">
                <label className="field-label">Time Zone</label>
                <select
                  name="timezone"
                  value={form.timezone}
                  onChange={handleFormChange}
                  disabled={!editing}
                  className="field-input"
                >
                  <option value="UTC-12:00">UTC-12:00</option>
                  <option value="UTC-08:00">UTC-08:00 Pacific Time</option>
                  <option value="UTC-07:00">UTC-07:00 Mountain Time</option>
                  <option value="UTC-06:00">UTC-06:00 Central Time</option>
                  <option value="UTC-05:00">UTC-05:00 Eastern Time</option>
                  <option value="UTC+00:00">UTC+00:00 London</option>
                  <option value="UTC+01:00">UTC+01:00 Central Europe</option>
                  <option value="UTC+03:00">UTC+03:00 Karachi/Riyadh</option>
                  <option value="UTC+05:00">UTC+05:00 Pakistan</option>
                  <option value="UTC+05:30">UTC+05:30 India</option>
                  <option value="UTC+08:00">UTC+08:00 Singapore</option>
                  <option value="UTC+09:00">UTC+09:00 Tokyo</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ACCOUNT INFO CARD ── */}
      <div className="profile-card">
        <div className="profile-card-header">
          <div>
            <h3>Account Information</h3>
            <p>View your account details and membership information.</p>
          </div>
        </div>
        <div className="account-info-grid">
          <div className="account-info-item">
            <div className="account-info-icon green">
              <span className="material-symbols-outlined">verified_user</span>
            </div>
            <div>
              <p className="account-info-label">Account Status</p>
              <span className="status active">Active</span>
            </div>
          </div>
          <div className="account-info-item">
            <div className="account-info-icon">
              <span className="material-symbols-outlined">shield_person</span>
            </div>
            <div>
              <p className="account-info-label">Role</p>
              <p className="account-info-value">
                {user?.role === "super_admin"
                  ? "Super Admin"
                  : user?.role === "moderator"
                    ? "Moderator"
                    : "Admin"}
              </p>
            </div>
          </div>

          <div className="account-info-item">
            <div className="account-info-icon">
              <span className="material-symbols-outlined">calendar_today</span>
            </div>
            <div>
              <p className="account-info-label">Member Since</p>
              <p className="account-info-value">
                {formatDate(user?.createdAt)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── DANGER ZONE ── */}
      <div className="profile-card danger-card">
        <div className="danger-zone-header">
          <div>
            <h3 className="danger-title">Danger Zone</h3>
            <p>
              Permanently delete your account and all associated data. This
              action cannot be undone.
            </p>
          </div>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={user?.role == "super_admin"}
            startIcon={
              <span className="material-symbols-outlined">delete</span>
            }
            onClick={() =>
              toast.error(
                "Please contact a super admin to delete your account.",
              )
            }
          >
            Delete Account
          </Button>
        </div>
      </div>
    </>
  );
};

export default ProfileInfo;
